import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular-messages';
import { TranslateService } from '@ngx-translate/core';
import { of, catchError, concatMap, map, switchMap, tap } from 'rxjs';
import { finalizeLoader } from '@app/shared/components';
import { ToastErrorService } from '@app/shared/services';
import { newsItemActions } from '../actions/news-item.actions';
import { HttpService } from '../services/http.service';

@Injectable()
export class NewsItemEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly toastErrorService = inject(ToastErrorService);

    getNewsItem$ = createEffect(() => {
        return this.actions.pipe(
            ofType(newsItemActions.getNewsItem),
            switchMap(({ id }) => {
                return this.httpService.getNewsItem(id).pipe(
                    finalizeLoader('news-item-get'),
                    map((newsItem) => newsItemActions.getNewsItemSuccess({ payload: newsItem })),
                    catchError((response: HttpErrorResponse) => of(newsItemActions.getNewsItemError({ code: response.status }))),
                );
            }),
        );
    });

    createNewsItem$ = createEffect(() => {
        return this.actions.pipe(
            ofType(newsItemActions.createNewsItem),
            concatMap(({ value }) => {
                return this.httpService.createNewsItem(value).pipe(
                    finalizeLoader('news-item-create'),
                    map((newsItem) => newsItemActions.createNewsItemSuccess({ payload: newsItem })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.news.created'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.news.createError');
                        return of(newsItemActions.createNewsItemError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    updateNewsItem$ = createEffect(() => {
        return this.actions.pipe(
            ofType(newsItemActions.updateNewsItem),
            concatMap(({ id, value }) => {
                return this.httpService.updateNewsItem(id, value).pipe(
                    finalizeLoader('news-item-update'),
                    map((newsItem) => newsItemActions.updateNewsItemSuccess({ payload: newsItem })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.news.updated'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.news.updateError');
                        return of(newsItemActions.updateNewsItemError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    deleteNewsItem$ = createEffect(() => {
        return this.actions.pipe(
            ofType(newsItemActions.deleteNewsItem),
            concatMap(({ id }) => {
                return this.httpService.deleteNewsItem(id).pipe(
                    finalizeLoader('news-item-delete'),
                    map(() => newsItemActions.deleteNewsItemSuccess({ id })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.news.deleted'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.news.deleteError');
                        return of(newsItemActions.deleteNewsItemError({ code: response.status }));
                    }),
                );
            }),
        );
    });
}
